/* globals chrome */
import sessionActions from '../actions/SessionActions';
import sessionReader from '../store/readHelpers/SessionReader';
import apiService from './api/ApiService';
import fileDeliveryService from './FileDeliveryService';
import storeManager from '../store/StoreManager';
import questionHelperService from './questionView/QuestionHelperServices';
import presignUrlService from './PresignUrlService';
import qtiConstants from '../qti/Constants';
import utilService from './UtilService';
import dataCleanUpService from './DataCleanUpService';
import additionalResourcesService from './AdditionalResourcesService';
import clientEventType from '../constants/ClientEventType';

let hasSentAdditionalResourcesToApp = false;

const chromeAppService = {
    /**
     * The Chrome App calls postMessage with extensionId triggering a message event
     */
    listenToInitEventFromApp() {
        const store = storeManager.getStore();

        const eventListener = (e) => {
            const { command, extensionId } = e.data;
            if (e.origin.indexOf('chrome') === 0 && command === 'init' && extensionId) {
                store.dispatch(sessionActions.setChromeExtensionId(extensionId));
                window.removeEventListener('message', eventListener);
                this.sendTestInfoToApp();
            }
        };
        window.addEventListener('message', eventListener);
    },
    sendTestInfoToApp() {
        if (sessionReader.getChromeExtensionId()) {
            this.sendTestTitle();
            this.sendAdditionalResourcesToChromeApp();
        }
    },
    sendClearTestSession() {
        if (sessionReader.getChromeExtensionId()) {
            chrome.runtime.sendMessage(sessionReader.getChromeExtensionId(), {
                command: 'clearTestSession',
            });
        }
    },
    sendCloseWindowMessage() {
        chrome.runtime.sendMessage(sessionReader.getChromeExtensionId(), {
            command: 'closeWindow',
        });
    },
    async exitChromeApp() {
        try {
            await dataCleanUpService.deleteAllDataFromAllAssessments(true);
            await apiService.notifyClientEvent(clientEventType.SEB_EXIT, '', true);
            this.sendCloseWindowMessage();
        } catch (error) {
            this.sendCloseWindowMessage();
        }
    },
    saveFile(blankDelivery = false) {
        const fileContents = fileDeliveryService.getEncryptedFileContent(blankDelivery);
        const fileName = fileDeliveryService.getFileName();
        chrome.runtime.sendMessage(sessionReader.getChromeExtensionId(), {
            command: 'saveFile',
            fileContents,
            fileName,
        });
    },
    sendTestTitle() {
        const state = storeManager.getState();
        const testTitle = state.parentTest?.title || state.assessment.title;
        chrome.runtime.sendMessage(sessionReader.getChromeExtensionId(), {
            command: 'setTestTitle',
            testTitle,
        });
    },
    findResourcesWithUrlsToSign() {
        const state = storeManager.getState();
        const { settings, assessment, parentTest, presignedUrls } = state;
        const additionalResources = additionalResourcesService.getAdditionalResources(settings, assessment, parentTest);
        const signedUrlsNeeded = [];

        additionalResources.forEach((resource) => {
            if (resource.type === qtiConstants.additionalResourceType.FILE && resource.contentItemId) {
                const { contentItemId } = resource;
                const presignedUrlForResource = presignedUrls[resource.contentItemId];
                const fileCiUrl = utilService.generateUrlForContentItemId(contentItemId, 'pdf', true);
                if (!presignedUrlForResource?.presignedUrl) {
                    const presignPromise = presignUrlService.loadPresignedUrl(contentItemId, fileCiUrl);
                    signedUrlsNeeded.push(presignPromise);
                }
            }
        });
        return signedUrlsNeeded;
    },
    getAdditionalResourcesWithPresignedUrls() {
        const pdfJsService = questionHelperService.getServices().pdfJsService;
        const state = storeManager.getState();
        const { settings, assessment, parentTest, presignedUrls } = state;
        const additionalResources = additionalResourcesService.getAdditionalResources(settings, assessment, parentTest);

        additionalResources.forEach((resource) => {
            const presignedUrl = presignedUrls[resource.contentItemId]?.presignedUrl;
            if (resource.type === qtiConstants.additionalResourceType.FILE && resource.contentItemId) {
                resource.pdfJsUrl = pdfJsService.getPdfJsUrl(presignedUrl);
                resource.rootUrl = qtiConstants.staticLibPaths.PDFJS;
            }
        });

        return additionalResources;
    },
    sendAdditionalResourcesToChromeApp() {
        Promise
            .all(this.findResourcesWithUrlsToSign())
            .then(() => {
                const additionalResources = this.getAdditionalResourcesWithPresignedUrls();

                if (!hasSentAdditionalResourcesToApp && additionalResources.length) {
                    chrome.runtime.sendMessage(sessionReader.getChromeExtensionId(), {
                        command: 'setAdditionalResources',
                        additionalResources,
                    });
                    hasSentAdditionalResourcesToApp = true;
                }
            }).catch((err) => {
                window.console.error('Failed setting additional resources for chrome app', err);
            });
    },

};

export default chromeAppService;
